import { SERVER_ROOT } from 'env';
import { PAGE_ONLY_ELEMENT_WIDTH } from 'constants/cardLayout';
import { createShapeId, toRichText } from 'tldraw';
import { postBoardAiEnrichClipboard } from '../api/boardAiApi';
import { useBoardStore } from '../store/useBoardStore';
import { boardLinkShapeProps } from './arrowTerminalLabels';
import { connectSelectedShapes } from './connectSelectedShapes';
import { boardAlert, boardPrompt } from './boardAlert';
import { extractBoardDocumentSnapshot } from './boardSnapshot';
import { flushBoardDocumentOps } from './persistBoardDocumentOps';
import { createPendingUrlActivityId } from './pendingUrlPin';
import { buildPasteCanvasEvidence, CANVAS_EVIDENCE_SOURCE, needsIntentSummary, OBJECT_TEXT_CAP } from './pasteCanvasEvidence';
import {
  MAX_ENRICH_IMAGE_BYTES,
  blobToBase64,
  normalizeClipboardImageFile,
  prepareImageFileForEnrich,
} from './clipboardImageUtils';
import { mergeAiCostIntoMeta, normalizeAiCost } from './aiCostFormat';
import { CLIPBOARD_PASTE_FALLBACK_LABEL, labelForClipboardContents, labelForClipboardKind } from './clipboardPasteLabel';
import { getPinCardSize } from './autoLayout';
import { parseYouTubeVideoId } from './youtubeUrl';

export { CLIPBOARD_PASTE_FALLBACK_LABEL, labelForClipboardContents, labelForClipboardKind } from './clipboardPasteLabel';

export { isPendingUrlActivityId } from './pendingUrlPin';
export { buildPasteCanvasEvidence } from './pasteCanvasEvidence';
export {
  MAX_ENRICH_IMAGE_BYTES,
  blobToBase64,
  normalizeClipboardImageFile,
  prepareImageFileForEnrich,
} from './clipboardImageUtils';

function parseUrlParts(url) {
  try {
    const parsed = new URL(url);
    return {
      href: parsed.href,
      protocol: parsed.protocol,
      host: parsed.host,
      hostname: parsed.hostname,
      pathname: parsed.pathname,
      search: parsed.search,
      hash: parsed.hash,
    };
  } catch {
    return {
      href: String(url || ''),
      protocol: 'https:',
      host: '',
      hostname: '',
      pathname: '',
      search: '',
      hash: '',
    };
  }
}

function buildPendingUrlActivity({ url, text = '' } = {}) {
  const normalizedUrl = extractClipboardUrl(url) || String(url || '').trim();
  const urlParts = parseUrlParts(normalizedUrl);
  const domain = (urlParts.hostname || urlParts.host || '').replace(/^www\./i, '') || 'Page';
  const isYouTube = Boolean(parseYouTubeVideoId(normalizedUrl));
  const pendingId = createPendingUrlActivityId();
  const description = String(text || '').trim();

  return {
    _id: pendingId,
    actionId: pendingId,
    type: 'COMMENT',
    pendingUrlLoad: true,
    pendingUrl: normalizedUrl,
    createdAt: new Date().toISOString(),
    action: {
      _id: pendingId,
      text: description,
      highlightData: buildPageOnlyHighlightData({ selectionText: description }),
    },
    content: [
      {
        _id: pendingId,
        title: isYouTube ? 'YouTube video' : domain,
        urls: [urlParts],
        metas: {},
        comments: description
          ? [
              {
                _id: pendingId,
                text: description,
                highlightData: buildPageOnlyHighlightData({ selectionText: description }),
              },
            ]
          : [],
      },
    ],
  };
}

/** Default empty frame size (matches AI arrange min frame / "adjacent" card footprint). */
export const DEFAULT_FRAME_WIDTH = 400;
export const DEFAULT_FRAME_HEIGHT = 300;

/** Default unbound connect-line length when placed from the context menu. */
export const DEFAULT_CONNECT_LINE_LENGTH = 160;

const URL_RE = /https?:\/\/[^\s<>"{}|\\^`[\]]+/i;

/**
 * @param {string} text
 * @returns {string|null}
 */
export function extractClipboardUrl(text) {
  const raw = String(text || '').trim();
  if (!raw) return null;
  try {
    const direct = new URL(raw);
    if (direct.protocol === 'http:' || direct.protocol === 'https:') return direct.href;
  } catch {
    /* fall through */
  }
  const match = raw.match(URL_RE);
  if (!match) return null;
  try {
    const parsed = new URL(match[0]);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') return parsed.href;
  } catch {
    return null;
  }
  return null;
}

/**
 * True when the whole clipboard string is an http(s) URL (not prose containing a link).
 * @param {string} text
 * @returns {boolean}
 */
export function isClipboardUrlOnly(text) {
  const raw = String(text || '').trim();
  if (!raw) return false;
  try {
    const direct = new URL(raw);
    return direct.protocol === 'http:' || direct.protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * Classify clipboard payload for paste-clipboard.
 * Image wins when present; otherwise a YouTube video URL, then any other
 * URL-only text; otherwise plain text.
 * @param {{ text?: string, imageFile?: File|null, hasImage?: boolean }} payload
 * @returns {'image'|'youtube'|'url'|'text'|null}
 */
export function classifyClipboardPayload({ text = '', imageFile = null, hasImage = false } = {}) {
  if (imageFile || hasImage) return 'image';
  const raw = String(text || '').trim();
  if (!raw) return null;
  if (isClipboardUrlOnly(raw)) {
    return parseYouTubeVideoId(raw) ? 'youtube' : 'url';
  }
  return 'text';
}

function readClipboardHtml(items, ms) {
  const read = (async () => {
    for (const item of items) {
      for (const type of item.types || []) {
        if (type === 'text/html') return (await item.getType(type)).text();
      }
    }
    return '';
  })();
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(''), ms);
    read.then(
      value => {
        clearTimeout(timer);
        resolve(value || '');
      },
      () => {
        clearTimeout(timer);
        resolve('');
      }
    );
  });
}

async function hasGrantedClipboardRead() {
  try {
    const status = await navigator.permissions?.query?.({ name: 'clipboard-read' });
    return status?.state === 'granted';
  } catch {
    return false;
  }
}

/**
 * Read enough of the clipboard to label the context menu, without image bytes.
 * Uses clipboard.read() only when permission is already granted so a right-click
 * cannot pop a permission dialog and dismiss the menu.
 * @returns {Promise<{ text: string, html: string, hasImage: boolean }|null>}
 */
async function readClipboardPeek() {
  if (typeof navigator === 'undefined') return null;

  if (navigator.clipboard?.read && (await hasGrantedClipboardRead())) {
    try {
      const items = await navigator.clipboard.read();
      let text = '';
      let hasImage = false;
      for (const item of items) {
        for (const type of item.types || []) {
          if (!hasImage && String(type).startsWith('image/')) hasImage = true;
          if (!text && type === 'text/plain') {
            text = await (await item.getType(type)).text();
          }
        }
      }
      // HTML can be a whole page. Only read it when a title or alt would
      // change the label, and don't let it hold the menu open.
      let html = '';
      if (hasImage || isClipboardUrlOnly(text)) {
        html = await readClipboardHtml(items, 250);
      }
      return { text, html, hasImage };
    } catch {
      /* fall through to readText */
    }
  }

  if (navigator.clipboard?.readText) {
    try {
      const text = await navigator.clipboard.readText();
      return { text, html: '', hasImage: false };
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Classify the current clipboard without loading image bytes.
 * @returns {Promise<'image'|'youtube'|'url'|'text'|null>}
 */
export async function peekClipboardKind() {
  const peek = await readClipboardPeek();
  if (!peek) return null;
  return classifyClipboardPayload(peek);
}

/**
 * Paste row label for the open context menu, or null when the clipboard is empty.
 * @returns {Promise<string|null>}
 */
export async function peekClipboardMenuLabel() {
  const peek = await readClipboardPeek();
  if (!peek) return null;
  const kind = classifyClipboardPayload(peek);
  if (!kind) return null;
  return labelForClipboardContents({ kind, text: peek.text, html: peek.html });
}

/**
 * Read clipboard via Clipboard API (image + text when available).
 * @returns {Promise<{ text: string, imageFile: File|null }>}
 */
export async function readClipboardPayload() {
  let text = '';
  let imageFile = null;

  if (navigator.clipboard?.read) {
    try {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        for (const type of item.types || []) {
          if (!imageFile && String(type).startsWith('image/')) {
            const blob = await item.getType(type);
            imageFile = normalizeClipboardImageFile(blob, type);
          }
          if (!text && type === 'text/plain') {
            text = await (await item.getType(type)).text();
          }
        }
      }
      return { text, imageFile };
    } catch {
      /* fall through to readText */
    }
  }

  if (navigator.clipboard?.readText) {
    text = await navigator.clipboard.readText();
  }
  return { text, imageFile };
}

/**
 * Extract clipboard payload from a browser paste event (no Clipboard API permission).
 * @param {ClipboardEvent} event
 * @returns {{ text: string, imageFile: File|null }|null}
 */
export function payloadFromPasteEvent(event) {
  const data = event?.clipboardData;
  if (!data) return null;

  let text = '';
  let imageFile = null;

  const items = data.items;
  if (items?.length) {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      const type = String(item?.type || '');
      if (!imageFile && type.startsWith('image/')) {
        const file = item.getAsFile?.();
        imageFile = normalizeClipboardImageFile(file, type);
      }
    }
  }

  if (!imageFile && data.files?.length) {
    for (let i = 0; i < data.files.length; i += 1) {
      const file = data.files[i];
      const normalized = normalizeClipboardImageFile(file, file?.type);
      if (normalized) {
        imageFile = normalized;
        break;
      }
    }
  }

  try {
    text = data.getData?.('text/plain') || '';
  } catch {
    text = '';
  }

  return { text, imageFile };
}

/**
 * True when the paste event carries tldraw's native shape clipboard
 * (`<div data-tldraw>...</div>`), so we should not override Ctrl+V.
 * @param {ClipboardEvent} event
 * @returns {boolean}
 */
export function isTldrawClipboardEvent(event) {
  const data = event?.clipboardData;
  if (!data?.getData) return false;
  try {
    const html = data.getData('text/html') || '';
    if (html.includes('data-tldraw')) return true;
    const text = data.getData('text/plain') || '';
    return text.includes('data-tldraw');
  } catch {
    return false;
  }
}

function applyMeaningMeta(editor, shapeIds, metaPatch) {
  if (!editor || !shapeIds?.length || !metaPatch) return;
  const updates = [];
  for (const id of shapeIds) {
    const shape = editor.getShape(id);
    if (!shape) continue;
    updates.push({
      id,
      type: shape.type,
      meta: {
        ...(shape.meta || {}),
        ...metaPatch,
        source: CANVAS_EVIDENCE_SOURCE.CLIPBOARD_PASTE,
        clipboardPaste: true,
      },
    });
  }
  if (updates.length) editor.updateShapes(updates);
}

/**
 * tldraw's text external-content handler creates a shape but does not select it
 * (unlike files/images). Diff page shape ids so enrich meta + blocker evidence
 * still attach after Ctrl+V / Paste clipboard.
 */
function findCreatedShapeIds(editor, beforeIds, typeFilter = null) {
  if (!editor) return [];
  const afterIds = editor.getCurrentPageShapeIds?.() || [];
  const created = [];
  for (const id of afterIds) {
    if (beforeIds.has(id)) continue;
    if (typeFilter) {
      const type = editor.getShape(id)?.type;
      if (!typeFilter.has(type)) continue;
    }
    created.push(id);
  }
  return created;
}

/** Persist paste/create via ops-only saver (never full /snapshot PATCH). */
async function persistCanvasDocument(editor, saveSnapshot) {
  if (!editor) return;
  try {
    const after = extractBoardDocumentSnapshot(editor);
    if (typeof saveSnapshot === 'function') {
      await saveSnapshot(after);
      return;
    }
    await flushBoardDocumentOps(editor);
  } catch {
    /* ignore */
  }
}

async function pasteClipboardTextAsShape(
  editor,
  pagePoint,
  text,
  { hashtag, saveSnapshot, analyzePinAdded } = {}
) {
  const content = String(text || '').trim();
  if (!content || !editor) return null;

  const beforeIds = new Set(editor.getCurrentPageShapeIds?.() || []);
  await editor.putExternalContent({
    type: 'text',
    text: content,
    point: pagePoint,
  });

  const targetIds = findCreatedShapeIds(
    editor,
    beforeIds,
    new Set(['text', 'note'])
  );
  if (targetIds.length) {
    try {
      editor.select(...targetIds);
    } catch {
      /* ignore */
    }
    applyMeaningMeta(editor, targetIds, {});
    if (needsIntentSummary(content) && hashtag) {
      useBoardStore.getState().setObjectProcessing(
        targetIds.map(id => ({ shapeId: id })),
        'enriching'
      );
    }
  }

  try {
    let intentSummary = '';
    let aiCost = null;
    let aiEnriched = false;
    if (needsIntentSummary(content) && hashtag) {
      try {
        const enriched = await postBoardAiEnrichClipboard(hashtag, {
          kind: 'text',
          text: content,
        });
        intentSummary = String(enriched?.intentSummary || '').trim();
        aiCost = normalizeAiCost(enriched?.aiCost);
        aiEnriched = Boolean(intentSummary);
      } catch (err) {
        console.warn('[paste-clipboard] text enrich failed', err?.message || err);
      }
    }
    // Short paste: classify the actual words. Long paste without summary: cap the dump.
    if (!intentSummary) {
      intentSummary = content.slice(0, OBJECT_TEXT_CAP);
    }
    if (targetIds.length && (intentSummary || aiCost)) {
      applyMeaningMeta(editor, targetIds, {
        ...(intentSummary ? { intentSummary } : {}),
        ...mergeAiCostIntoMeta({}, aiCost),
      });
    }

    await persistCanvasDocument(editor, saveSnapshot);

    const shapeId = targetIds[0] || null;
    if (shapeId && !editor.getShape(shapeId)) return shapeId;
    const evidence = buildPasteCanvasEvidence({
      shapeId,
      kind: 'text',
      rawText: content,
      intentSummary,
      aiEnriched,
    });
    if (evidence && typeof analyzePinAdded === 'function') {
      analyzePinAdded({ canvasEvidence: evidence, placePending: false });
    }
    return shapeId;
  } finally {
    if (targetIds.length) {
      useBoardStore.getState().clearObjectProcessing(
        targetIds.map(id => ({ shapeId: id })),
        { onlyPhase: 'enriching' }
      );
    }
  }
}

async function pasteClipboardImageAsShape(
  editor,
  pagePoint,
  imageFile,
  { hashtag, saveSnapshot, analyzePinAdded } = {}
) {
  if (!editor || !imageFile) return null;

  // Prefer declared type; clipboard screenshots sometimes arrive with empty File.type.
  const pasteFile = normalizeClipboardImageFile(
    imageFile,
    imageFile.type || 'image/png'
  );
  if (!pasteFile) return null;
  const beforeIds = new Set(editor.getCurrentPageShapeIds?.() || []);
  await editor.putExternalContent({
    type: 'files',
    files: [pasteFile],
    point: pagePoint,
  });

  // Files handler usually selects created shapes; page-id diff is authoritative
  // (same race class as text paste if selection is cleared during upload).
  let targetIds = findCreatedShapeIds(editor, beforeIds, new Set(['image']));
  if (!targetIds.length) {
    targetIds = [...editor.getSelectedShapeIds()].filter((id) => {
      const shape = editor.getShape(id);
      return shape?.type === 'image' && !beforeIds.has(id);
    });
  }
  if (targetIds.length) {
    try {
      editor.select(...targetIds);
    } catch {
      /* ignore */
    }
  } else {
    console.warn('[paste-clipboard] image shape missing after putExternalContent');
    return null;
  }

  applyMeaningMeta(editor, targetIds, {});
  useBoardStore.getState().setObjectProcessing(
    targetIds.map(id => ({ shapeId: id })),
    'enriching'
  );

  try {
    let intentSummary = '';
    let ocrText = '';
    let aiCost = null;
    const enrichFile = await prepareImageFileForEnrich(pasteFile, MAX_ENRICH_IMAGE_BYTES);
    if (!enrichFile) {
      console.warn(
        '[paste-clipboard] image too large to enrich',
        pasteFile.size,
        pasteFile.type
      );
    } else {
      try {
        const imageBase64 = await blobToBase64(enrichFile);
        const enriched = await postBoardAiEnrichClipboard(hashtag, {
          kind: 'image',
          imageBase64,
          mimeType: enrichFile.type || pasteFile.type || 'image/png',
        });
        intentSummary = String(enriched?.intentSummary || '').trim();
        ocrText = String(enriched?.ocrText || '').trim();
        aiCost = normalizeAiCost(enriched?.aiCost);
      } catch (err) {
        console.warn('[paste-clipboard] image enrich failed', err?.message || err);
      }
    }

    applyMeaningMeta(editor, targetIds, {
      ...(intentSummary ? { intentSummary } : {}),
      ...(ocrText ? { ocrText } : {}),
      ...mergeAiCostIntoMeta({}, aiCost),
    });

    await persistCanvasDocument(editor, saveSnapshot);

    const shapeId = targetIds[0] || null;
    if (shapeId && !editor.getShape(shapeId)) return shapeId;
    const evidence = buildPasteCanvasEvidence({
      shapeId,
      kind: 'image',
      intentSummary,
      ocrText,
      aiEnriched: Boolean(intentSummary) && needsIntentSummary(ocrText),
    });
    if (evidence && typeof analyzePinAdded === 'function') {
      analyzePinAdded({ canvasEvidence: evidence, placePending: false });
    }
    return shapeId;
  } finally {
    useBoardStore.getState().clearObjectProcessing(
      targetIds.map(id => ({ shapeId: id })),
      { onlyPhase: 'enriching' }
    );
  }
}

/** Page-only highlight snapshot — same contract as extension `buildPageOnlyHighlightSnapshot`. */
export function buildPageOnlyHighlightData({ selectionText = '' } = {}) {
  return {
    placementMode: 'absolute',
    saveScope: 'page',
    locator: null,
    selectionOffsetDocTop: null,
    selectionOffsetDocLeft: null,
    highlightDocTop: 0,
    highlightDocLeft: 0,
    // Width drives grid span / canvas card width; height is omitted so the
    // card stays flexible to og:image / content (measured on canvas).
    elementWidth: PAGE_ONLY_ELEMENT_WIDTH,
    selectionText: String(selectionText || '').slice(0, 300),
    selectionTextSource: 'page',
  };
}

/** Stub host positionData required by POST /api/comment validation. */
export function buildStubPositionData() {
  const w = PAGE_ONLY_ELEMENT_WIDTH;
  return {
    absoluteTop: 0,
    absoluteLeft: 0,
    documentWidth: w,
    documentHeight: w,
    bodyWidth: w,
    bodyHeight: w,
    windowWidth: w,
    windowHeight: w,
  };
}

function nextFrameLogicalId() {
  return `manual-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Place an empty tldraw text shape at a page point (same type as the text tool).
 */
export function addTextAtPoint(editor, { x, y } = {}) {
  if (!editor) return null;
  const pageX = Number.isFinite(Number(x)) ? Number(x) : 80;
  const pageY = Number.isFinite(Number(y)) ? Number(y) : 80;
  const shapeId = createShapeId(
    `text-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  );
  const scale = editor.user?.getIsDynamicResizeMode?.()
    ? 1 / (editor.getZoomLevel?.() || 1)
    : 1;

  editor.run(() => {
    editor.markHistoryStoppingPoint('before-add-text');
    editor.createShape({
      id: shapeId,
      type: 'text',
      x: pageX,
      y: pageY,
      props: {
        autoSize: true,
        w: 20,
        scale,
        richText: toRichText(''),
      },
    });
    editor.select(shapeId);
  });

  return shapeId;
}

/**
 * Enter text editing so the user can type immediately.
 * Deferred so context-menu close / click-through cannot cancel editing.
 */
function beginEditingText(editor, shapeId) {
  const shape = editor.getShape(shapeId);
  if (!shape) return;

  editor.select(shapeId);
  editor.setEditingShape(shapeId);
  editor.setCurrentTool('select.editing_shape', {
    target: 'shape',
    shape,
  });

  const focusTextInput = () => {
    if (editor.getEditingShapeId() !== shapeId) return;
    const input = editor
      .getContainer()
      ?.querySelector?.('.tl-text-shape-label [contenteditable="true"], .tl-rich-text [contenteditable="true"]');
    if (!input) return;
    input.focus();
  };

  editor.timers.requestAnimationFrame(() => {
    focusTextInput();
    editor.timers.setTimeout(focusTextInput, 0);
  });
}

/**
 * Context-menu: create a tldraw text shape at the right-click page point and start typing.
 */
export function addTextFromContext(editor, pagePoint, { saveSnapshot } = {}) {
  if (!editor || editor.getInstanceState()?.isReadonly) return null;

  const before = extractBoardDocumentSnapshot(editor);
  const shapeId = addTextAtPoint(editor, {
    x: pagePoint?.x,
    y: pagePoint?.y,
  });
  if (!shapeId) return null;

  try {
    if (typeof saveSnapshot === 'function') {
      saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      void flushBoardDocumentOps(editor, before);
    }
  } catch {
    /* ignore */
  }

  // Wait for the menu to close and any click-through to settle; otherwise
  // EditingShape treats a pointer on the new text as "cancel edit".
  editor.timers.setTimeout(() => beginEditingText(editor, shapeId), 50);

  return shapeId;
}

/**
 * Place an empty named frame at a page point (persisted via tldraw snapshot).
 */
export function addFrameAtPoint(editor, { name, x, y, w = DEFAULT_FRAME_WIDTH, h = DEFAULT_FRAME_HEIGHT } = {}) {
  if (!editor) return null;
  const label = String(name || '').trim() || 'group name';
  const logicalId = nextFrameLogicalId();
  const frameId = createShapeId(`frame-${logicalId}`);
  const pageX = Number.isFinite(Number(x)) ? Number(x) : 80;
  const pageY = Number.isFinite(Number(y)) ? Number(y) : 80;

  editor.run(() => {
    editor.markHistoryStoppingPoint('before-add-frame');
    editor.createShape({
      id: frameId,
      type: 'frame',
      x: pageX,
      y: pageY,
      props: {
        w: Number(w) || DEFAULT_FRAME_WIDTH,
        h: Number(h) || DEFAULT_FRAME_HEIGHT,
        name: label,
      },
      meta: { boardFrameId: logicalId },
    });
    editor.select(frameId);
  });

  return frameId;
}

/**
 * Enter frame title editing and focus the input so the user can type immediately.
 * Deferred so context-menu close / click-through cannot cancel editing.
 */
function beginEditingFrameTitle(editor, frameId) {
  const shape = editor.getShape(frameId);
  if (!shape) return;

  editor.select(frameId);
  editor.setEditingShape(frameId);
  editor.setCurrentTool('select.editing_shape', {
    target: 'shape',
    shape,
  });

  const focusTitleInput = () => {
    if (editor.getEditingShapeId() !== frameId) return;
    const input = editor
      .getContainer()
      ?.querySelector?.('.tl-frame-label__editing .tl-frame-name-input');
    if (!input) return;
    input.focus();
    input.select();
  };

  // Wait for React to mount the editing input, then beat any focus restore.
  editor.timers.requestAnimationFrame(() => {
    focusTitleInput();
    editor.timers.setTimeout(focusTitleInput, 0);
  });
}

/**
 * Context-menu: create a frame at the right-click page point and focus its title.
 */
export function addFrameFromContext(editor, pagePoint, { saveSnapshot } = {}) {
  if (!editor || editor.getInstanceState()?.isReadonly) return null;

  const before = extractBoardDocumentSnapshot(editor);
  const frameId = addFrameAtPoint(editor, {
    name: 'group name',
    x: pagePoint?.x,
    y: pagePoint?.y,
  });
  if (!frameId) return null;

  try {
    if (typeof saveSnapshot === 'function') {
      saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      void flushBoardDocumentOps(editor, before);
    }
  } catch {
    /* ignore */
  }

  // Wait for the menu to close and any click-through to settle; otherwise
  // EditingShape treats a pointer on the new frame as "cancel rename".
  editor.timers.setTimeout(() => beginEditingFrameTitle(editor, frameId), 50);

  return frameId;
}

/**
 * Place an unbound board connect line centered on a page point.
 * Ends are free — drag them onto pins, notes, or frames to bind.
 */
export function addConnectLineAtPoint(
  editor,
  { x, y, length = DEFAULT_CONNECT_LINE_LENGTH } = {}
) {
  if (!editor) return null;
  const pageX = Number.isFinite(Number(x)) ? Number(x) : 80;
  const pageY = Number.isFinite(Number(y)) ? Number(y) : 80;
  const span = Math.max(40, Number(length) || DEFAULT_CONNECT_LINE_LENGTH);
  const half = span / 2;
  const arrowId = createShapeId(
    `link-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  );

  editor.run(() => {
    editor.markHistoryStoppingPoint('before-add-connect-line');
    editor.createShape({
      id: arrowId,
      type: 'arrow',
      x: pageX - half,
      y: pageY,
      props: boardLinkShapeProps({
        start: { x: 0, y: 0 },
        end: { x: span, y: 0 },
        color: 'black',
        size: 'm',
      }),
    });
    editor.select(arrowId);
  });

  return arrowId;
}

/**
 * Context-menu: create a connect line at the right-click page point.
 */
export async function addConnectLineFromContext(editor, pagePoint, { saveSnapshot } = {}) {
  if (!editor || editor.getInstanceState()?.isReadonly) return null;

  const before = extractBoardDocumentSnapshot(editor);
  const arrowId = addConnectLineAtPoint(editor, {
    x: pagePoint?.x,
    y: pagePoint?.y,
  });
  if (!arrowId) return null;

  try {
    if (typeof saveSnapshot === 'function') {
      await saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      await flushBoardDocumentOps(editor, before);
    }
  } catch {
    /* ignore */
  }

  return arrowId;
}

/**
 * Context-menu: connect the two selected connectable objects with a line.
 */
export async function connectSelectedShapesFromContext(editor, { saveSnapshot } = {}) {
  if (!editor || editor.getInstanceState()?.isReadonly) return null;

  const before = extractBoardDocumentSnapshot(editor);
  const arrowId = connectSelectedShapes(editor);
  if (!arrowId) return null;

  try {
    if (typeof saveSnapshot === 'function') {
      await saveSnapshot(extractBoardDocumentSnapshot(editor));
    } else {
      await flushBoardDocumentOps(editor, before);
    }
  } catch {
    /* ignore */
  }

  return arrowId;
}

/**
 * Create a page-only user pin from a URL via the same POST /api/comment path
 * as extension "Just save the page meta" (saveScope: 'page').
 *
 * Shows an optimistic loading pin on the canvas immediately, then fills in
 * scraped title/meta when the request finishes (socket/loadBoard replaces it
 * with the real activity).
 */
export async function createPageOnlyPinFromUrl({
  url,
  text = '',
  hashtag,
  pagePoint,
  isCommentPublic = true,
} = {}) {
  const normalizedUrl = extractClipboardUrl(url) || String(url || '').trim();
  if (!normalizedUrl) throw new Error('A valid URL is required');
  const tagName = String(hashtag || '').trim();
  if (!tagName) throw new Error('Board hashtag is required');

  const description = String(text || '').trim();
  const hashtags = [{ name: tagName, loweredName: tagName.toLowerCase() }];
  const store = useBoardStore.getState();
  const pendingActivity = buildPendingUrlActivity({ url: normalizedUrl, text: description });
  const isYouTube = Boolean(parseYouTubeVideoId(normalizedUrl));
  const cardSize = isYouTube ? getPinCardSize(pendingActivity) : { width: 300, height: 400 };
  const preferredPlacement =
    pagePoint &&
    Number.isFinite(Number(pagePoint.x)) &&
    Number.isFinite(Number(pagePoint.y))
      ? {
          x: Number(pagePoint.x),
          y: Number(pagePoint.y),
          width: cardSize.width,
          height: cardSize.height,
        }
      : {
          x: 80,
          y: 80,
          width: cardSize.width,
          height: cardSize.height,
        };
  const pendingId = pendingActivity._id;
  store.prependActivity(pendingActivity);
  const existingPlacements = store.board?.placements || [];
  store.updatePlacementsLocal([
    ...existingPlacements.filter(p => String(p.activityId) !== pendingId),
    {
      activityId: pendingId,
      x: preferredPlacement.x,
      y: preferredPlacement.y,
      width: preferredPlacement.width,
      height: preferredPlacement.height,
      zIndex: existingPlacements.length,
      placementSource: 'pending-url',
    },
  ]);

  const body = {
    text: description,
    hashtags,
    persontags: [],
    isCommentPublic: Boolean(isCommentPublic),
    contentPayload: {
      contentType: 'HTTP',
      url: normalizedUrl,
      documentTitle: '',
      // headHTML omitted — server scrapes via findOrCreateWithPayload (same Content path).
    },
    positionData: buildStubPositionData(),
    highlightData: buildPageOnlyHighlightData({
      selectionText: description,
    }),
    preferredPlacement,
  };

  try {
    const res = await fetch(`${SERVER_ROOT}/api/comment`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    let json = null;
    try {
      json = await res.json();
    } catch {
      json = null;
    }

    if (!res.ok) {
      const message = json?.message || res.statusText || 'Failed to save pin';
      const err = new Error(message);
      err.status = res.status;
      err.errors = json?.errors;
      throw err;
    }

    // Scrape finished — update title on the loading card; keep pendingUrlLoad until
    // socket/loadBoard swaps in the real activity with full meta/thumbnail.
    const scrapedTitle = String(json?.content?.title || '').trim();
    if (scrapedTitle) {
      const current = useBoardStore
        .getState()
        .activities.find(a => String(a._id || a.actionId) === pendingId);
      const content0 = current?.content?.[0];
      if (content0) {
        useBoardStore.getState().updateActivityLocal(pendingId, {
          content: [{ ...content0, title: scrapedTitle }],
        });
      }
    }

    return { ...json, pendingActivityId: pendingId };
  } catch (err) {
    useBoardStore.getState().removeActivitiesLocal([pendingId]);
    throw err;
  }
}

/**
 * Context-menu / Ctrl+V: Paste clipboard
 * - YouTube URL → page-only video card
 * - other URL → page-only pin flow
 * - other text → tldraw text shape + intent summary
 * - image → tldraw image shape + OCR/intent summary
 * @param {{ hashtag?: string, saveSnapshot?: Function, analyzePinAdded?: Function, payload?: { text?: string, imageFile?: File|null } }} [options]
 */
export async function promptAndPasteClipboard(
  editor,
  pagePoint,
  { hashtag, saveSnapshot, analyzePinAdded, payload: providedPayload } = {}
) {
  if (!editor || editor.getInstanceState()?.isReadonly) return null;

  let payload = providedPayload || null;
  if (!payload) {
    try {
      payload = await readClipboardPayload();
    } catch (err) {
      await boardAlert({
        title: CLIPBOARD_PASTE_FALLBACK_LABEL,
        message: 'Could not read the clipboard. Allow clipboard access and try again.',
      });
      return null;
    }
  }

  const kind = classifyClipboardPayload(payload);
  const title = labelForClipboardKind(kind);
  if (!kind) {
    await boardAlert({
      title,
      message: 'Clipboard is empty.',
    });
    return null;
  }

  if (kind === 'url' || kind === 'youtube') {
    const url = extractClipboardUrl(payload.text);
    const description = await boardPrompt({
      title,
      message: url,
      placeholder: 'Description (optional)',
      confirmLabel: kind === 'youtube' ? 'Add video' : 'Add pin',
      required: false,
    });
    if (description == null) return null;

    try {
      return await createPageOnlyPinFromUrl({
        url,
        text: description,
        hashtag,
        pagePoint,
      });
    } catch (err) {
      await boardAlert({
        title,
        message: err?.message || 'Failed to add pin from URL.',
      });
      return null;
    }
  }

  if (kind === 'text') {
    try {
      return await pasteClipboardTextAsShape(editor, pagePoint, payload.text, {
        hashtag,
        saveSnapshot,
        analyzePinAdded,
      });
    } catch (err) {
      await boardAlert({
        title,
        message: err?.message || 'Failed to paste text.',
      });
      return null;
    }
  }

  try {
    return await pasteClipboardImageAsShape(editor, pagePoint, payload.imageFile, {
      hashtag,
      saveSnapshot,
      analyzePinAdded,
    });
  } catch (err) {
    await boardAlert({
      title,
      message: err?.message || 'Failed to paste image.',
    });
    return null;
  }
}
